/**
 * BOTH ENDS OF THE PROVIDER ROUND TRIP, as pure decisions plus one Node-callable
 * orchestration -- shared by `/tabelle` (Google, Task 11) and `/ebay` (eBay,
 * Task 12), so the two views cannot drift apart on the question that matters.
 *
 * THE DEPARTURE (`startProviderConnect`). `POST /admin-api/<p>/oauth-start`
 * answers `authorizeUrl`, and since plan 3b amendment A1 that value is a
 * HANDOFF url on our own public API origin, not the provider's consent screen.
 * The browser must reach it by TOP-LEVEL NAVIGATION -- see lib/navigation.ts
 * for the whole argument. This module owns that call so no view can do it a
 * different way.
 *
 * THE ARRIVAL (`classifyLanding`, `runLandingFlow`). The API's callback bounces
 * the browser back to `/tabelle?…` or `/ebay?…` with exactly one of `?state=`
 * or `?error=` (api/src/adminRoutes.ts's `adminRedirect`). Which one it is
 * decides what the page says, so the classification is pure and complete over
 * every landing the merged callback can produce -- a decision left in JSX is a
 * decision no test can drive.
 *
 * REVIEW POINT B2 lives in `runLandingFlow`, and it is an ORDERING, not a
 * side effect that happens eventually: `io.clearSearch()` is the first
 * statement, unconditionally, before any request leaves. Consequences, all
 * intended: `?state=<jti>` never enters the history stack, a reload cannot
 * re-submit a consumed single-use state, and nothing carries the jti in a
 * `Referer` (belt to index.html's `referrer=same-origin`).
 *
 * THE N3 RESIDUAL lives in `connectScreen`. `400 invalid_state` is the one
 * error whose most likely cause is an attack, and the person looking at it is
 * the only one who can revoke the grant -- so it renders a SECURITY screen
 * naming the situation and linking the provider's own revocation page, never a
 * generic "Verbindung fehlgeschlagen".
 */
import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { assignTopLevel } from './navigation';
import { t } from './i18n';
import { errorMessage } from '../components/ErrorNotice';
import { reportAuthFailure } from '../queries';
import type { LandingQuery } from './router';
import type { OAuthStart } from './types';

export type Provider = 'google' | 'ebay';

/** Presentation only. Literal t() calls, one per provider, so
 *  test/i18nKeys.test.ts can prove both strings exist. */
export function providerName(provider: Provider): string {
  switch (provider) {
    case 'google': return t('connect.provider.google');
    case 'ebay': return t('connect.provider.ebay');
  }
}

// ── the landing ──────────────────────────────────────────────────────────────

/**
 * What a browser arriving on /tabelle or /ebay is actually looking at.
 *
 * The API's callback bounces here with exactly one of:
 *   ?state=<jti>       the provider's code is parked; finish it
 *   ?error=<code>      consent denied, missing code, state_expired,
 *                      browser_mismatch
 *   (nothing)          a normal visit
 *
 * Pure so that Task 12's DOM suite can drive every outcome without a server,
 * and so the classification cannot drift between the two views.
 *
 * `mismatch` IS A DEVIATION FROM task-11-brief.md, which names five landings.
 * Plan 3b amendment A1 (`phase3/oauth-bind`, commit 3a4b040) landed a SIXTH
 * error code after the brief was written: the callback now refuses to park a
 * code unless the browser presents `__Host-oauth_bind`, and answers
 * `?error=browser_mismatch` when it does not. Falling through to `denied`
 * would render "Sie haben die Freigabe abgebrochen" over the single landing
 * whose signature IS the consent-phishing attack A1 exists to blunt: the
 * victim consented on a genuine provider screen, in a browser that never
 * started the flow. It gets a security screen of its own instead -- one that
 * names BOTH readings honestly, because a blocked cookie or a flow finished in
 * a different browser produces the identical code.
 */
export type Landing =
  | { kind: 'none' }
  | { kind: 'finish'; state: string }
  | { kind: 'denied'; code: string }
  | { kind: 'expired' }
  | { kind: 'mismatch' }
  | { kind: 'invalid' };

/** The error codes the merged callback emits itself, as opposed to passing
 *  through from the provider (api/src/adminRoutes.ts's `handleOAuthCallback`). */
const EXPIRED = 'state_expired';
const BROWSER_MISMATCH = 'browser_mismatch';

/** The jti is a UUID minted by `newId()`; nothing else came from our callback. */
const JTI = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function classifyLanding(search: URLSearchParams): Landing {
  const error = search.get('error');
  // `error` WINS OVER `state`: the callback never sends both, and if something
  // ever did, believing the failure is the fail-safe order.
  if (error) {
    if (error === EXPIRED) return { kind: 'expired' };
    if (error === BROWSER_MISMATCH) return { kind: 'mismatch' };
    // access_denied (the user said no) and missing_code both mean "no consent
    // happened". The provider's own code is carried for the caller's log line
    // and never rendered raw.
    return { kind: 'denied', code: error };
  }
  const state = search.get('state');
  // ABSENT is a normal visit; PRESENT-BUT-NOT-A-JTI is not, and the two are
  // different values here (`null` vs `''`). task-11-brief.md Step 5 is
  // explicit that an empty `?state=` classifies `invalid` -- a hand-edited
  // state is not an ordinary arrival, and it must never be sent to the server.
  if (state === null) return { kind: 'none' };
  if (!JTI.test(state)) return { kind: 'invalid' };
  return { kind: 'finish', state };
}

/**
 * Router.tsx hands views the ALREADY PARSED `?state=`/`?error=` pair
 * (lib/router.ts's `parseLanding`), while `classifyLanding` and
 * `<ConnectOutcome>` take a `URLSearchParams` -- the shape Task 12's DOM suite
 * constructs directly. This is the one adapter between them, here rather than
 * in a view, so no component builds a query string of its own.
 */
export function searchParamsFromLandingQuery(query: LandingQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.error !== undefined) params.set('error', query.error);
  if (query.state !== undefined) params.set('state', query.state);
  return params;
}

// ── what the page is showing ─────────────────────────────────────────────────

/**
 * `foreignFlow` -- this browser's session did not initiate the flow it is
 * trying to finish (a `400 invalid_state`), or the URL carried a state our
 * callback never minted. `browserMismatch` -- A1's callback refused to park
 * the code because this browser holds no binding cookie.
 */
export type SecurityReason = 'foreignFlow' | 'browserMismatch';

export type Outcome<R> =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'success'; result: R }
  | { kind: 'denied'; code: string }
  | { kind: 'expired' }
  | { kind: 'failed'; message: string }
  | { kind: 'security'; reason: SecurityReason };

/**
 * Every landing EXCEPT the one that has to ask the server. An exhaustive
 * switch with a declared return type: adding a `Landing` kind without deciding
 * what it SHOWS is a TypeScript error here, not a case that quietly falls
 * through to whatever the last branch happened to be.
 */
export function immediateOutcome(landing: Exclude<Landing, { kind: 'finish' }>): Outcome<never> {
  switch (landing.kind) {
    case 'none': return { kind: 'idle' };
    case 'denied': return { kind: 'denied', code: landing.code };
    case 'expired': return { kind: 'expired' };
    case 'mismatch': return { kind: 'security', reason: 'browserMismatch' };
    case 'invalid': return { kind: 'security', reason: 'foreignFlow' };
  }
}

/** The outcome a landing already has before any effect runs, so the FIRST
 *  paint is already truthful -- no frame of "nothing happened" in front of a
 *  security warning. */
export function initialOutcome<R>(landing: Landing): Outcome<R> {
  return landing.kind === 'finish' ? { kind: 'pending' } : immediateOutcome(landing);
}

/**
 * A failed `oauth-finish`, classified.
 *
 * `invalid_state` is matched on the CODE alone, not on `400 && code` -- the
 * merged handlers answer it only with 400, and if that ever changed, erring
 * toward the security screen is the fail-safe direction. Everything else
 * (`exchange_failed`, a 5xx, a dropped connection) is an ordinary failure with
 * the shell's usual `error.<code>` copy.
 */
export function finishFailureOutcome<R>(error: unknown): Outcome<R> {
  if (error instanceof ApiError && error.code === 'invalid_state') {
    return { kind: 'security', reason: 'foreignFlow' };
  }
  return { kind: 'failed', message: errorMessage(error) };
}

// ── the orchestration (STANDING RULE: callable from Node) ────────────────────

export interface LandingFlowIO<R> {
  /** Task 2's `clearSearch` (Router.tsx): history.replaceState, no navigation. */
  clearSearch: () => void;
  /** POST /admin-api/<p>/oauth-finish { state }. */
  finish: (state: string) => Promise<R>;
  setOutcome: (outcome: Outcome<R>) => void;
}

/**
 * REVIEW POINT B2, AS AN ORDERING. `io.clearSearch()` is the first statement
 * and runs for EVERY landing kind, including `none` -- unconditionally,
 * because a condition here is a branch in which a mutation could hide, and
 * because "the state token is gone before anything else happens" is a stronger
 * property than "the state token is gone when it was present".
 *
 * Only `finish` talks to the server. Everything else is decided from the URL
 * alone (`immediateOutcome`), which is what makes a hand-edited `?state=`
 * something the server never sees.
 *
 * Called exactly once per mount by <ConnectOutcome>, ref-guarded there: the
 * parked code is single-use, and React 18 StrictMode double-invokes effects in
 * development, so an unguarded second call would answer `invalid_state` to the
 * user's own legitimate connect -- a self-inflicted security screen.
 */
export async function runLandingFlow<R>(landing: Landing, io: LandingFlowIO<R>): Promise<void> {
  io.clearSearch();
  if (landing.kind !== 'finish') {
    io.setOutcome(immediateOutcome(landing));
    return;
  }
  io.setOutcome({ kind: 'pending' });
  try {
    io.setOutcome({ kind: 'success', result: await io.finish(landing.state) });
  } catch (error) {
    io.setOutcome(finishFailureOutcome(error));
  }
}

// ── the departure (STANDING RULE: callable from Node) ────────────────────────

export interface StartConnectIO {
  /** POST /admin-api/<p>/oauth-start. */
  start: () => Promise<OAuthStart>;
  setBusy: (busy: boolean) => void;
  setError: (message: string | null) => void;
  /**
   * Only for the 401 (final review I-1). The departure is an ACTION like any
   * other -- it POSTs, it mints a state row server-side -- and a session that
   * expired while the operator was reading the consent copy would otherwise
   * produce a generic failure under a shell still naming them.
   */
  queryClient: QueryClient;
}

/**
 * TOP-LEVEL NAVIGATION TO THE HANDOFF URL, and nothing else (A1; see
 * lib/navigation.ts). No view builds this call itself, so "did the connect
 * navigate or did it fetch?" has exactly one answer for both providers.
 *
 * `setBusy(false)` deliberately does NOT run on the success path: the page is
 * leaving, and re-enabling the button would invite a second `oauth-start`
 * whose state supersedes the one the browser is already carrying a cookie for.
 */
export async function startProviderConnect(io: StartConnectIO): Promise<boolean> {
  io.setBusy(true);
  io.setError(null);
  try {
    const { authorizeUrl } = await io.start();
    if (!assignTopLevel(authorizeUrl)) {
      // A non-navigable scheme cannot come from our own BFF; if it ever does,
      // saying so beats handing it to location.assign (lib/navigation.ts).
      io.setError(t('connect.error.badAuthorizeUrl'));
      io.setBusy(false);
      return false;
    }
    return true;
  } catch (error) {
    reportAuthFailure(io.queryClient, error);
    io.setError(errorMessage(error));
    io.setBusy(false);
    return false;
  }
}

// ── the screen ───────────────────────────────────────────────────────────────

export interface RevocationLink {
  href: string;
  label: string;
  /** The navigation path, for the provider whose deep link is not stable. */
  hint: string | null;
}

/**
 * The provider's OWN revocation page. Rendered `rel="noopener noreferrer"` at
 * the call site; `referrer=same-origin` (index.html) already strips the
 * referrer on any cross-origin navigation, and the `rel` is the belt.
 *
 * eBay has no stable deep link to its third-party-access list, so its entry
 * carries the navigation path as well -- naming a URL that 404s would be worse
 * than naming the two clicks that get there.
 */
const REVOCATION: Record<Provider, RevocationLink> = {
  google: {
    href: 'https://myaccount.google.com/permissions',
    label: t('connect.revoke.google'),
    hint: null,
  },
  ebay: {
    href: 'https://accounts.ebay.de/acctsec/security-center',
    label: t('connect.revoke.ebay'),
    hint: t('connect.revoke.ebayPath'),
  },
};

export function revocationLink(provider: Provider): RevocationLink {
  return REVOCATION[provider];
}

export type ScreenTone = 'security' | 'calm' | 'progress';

export interface ConnectScreen {
  tone: ScreenTone;
  /** `alert` for anything the operator must act on, `status` otherwise. */
  role: 'alert' | 'status';
  title: string;
  body: string;
  /** Present on a SECURITY screen and nowhere else. */
  revocation: RevocationLink | null;
  supportNote: string | null;
  /** The label of a "erneut versuchen" button, or null when retrying is not
   *  the right answer (a security screen must not invite another attempt). */
  retry: string | null;
}

export const SUPPORT_MAIL = 'support@stack-stock.com';

/**
 * THE WHOLE RENDERING DECISION, in one pure function -- <ConnectOutcome>
 * renders this record and computes nothing of its own, so "did the security
 * screen degrade into a generic error?" is a Node assertion rather than a
 * question only a rendered DOM could answer.
 *
 * `null` means the component renders no screen at all: `idle` (an ordinary
 * visit) and `success` (the provider-specific confirmation takes over).
 *
 * Exhaustive switch with a declared return type: a new `Outcome` kind is a
 * TypeScript error here until it has been given a screen.
 */
export function connectScreen<R>(outcome: Outcome<R>, provider: Provider): ConnectScreen | null {
  switch (outcome.kind) {
    case 'idle':
    case 'success':
      return null;
    case 'pending':
      return {
        tone: 'progress',
        role: 'status',
        title: t('connect.pending.title'),
        body: t('connect.pending.body', { provider: providerName(provider) }),
        revocation: null,
        supportNote: null,
        retry: null,
      };
    case 'denied':
      return {
        tone: 'calm',
        role: 'status',
        title: t('connect.denied.title'),
        body: t('connect.denied.body', { provider: providerName(provider) }),
        revocation: null,
        supportNote: null,
        retry: t('connect.retry'),
      };
    case 'expired':
      return {
        tone: 'calm',
        role: 'status',
        title: t('connect.expired.title'),
        body: t('connect.expired.body'),
        revocation: null,
        supportNote: null,
        retry: t('connect.retry'),
      };
    case 'failed':
      return {
        tone: 'calm',
        role: 'alert',
        // The server's own message, resolved by `errorMessage` when the
        // outcome was built -- never a guess about what went wrong.
        title: t('connect.failed.title'),
        body: outcome.message,
        revocation: null,
        supportNote: null,
        retry: t('connect.retry'),
      };
    case 'security':
      return securityScreen(outcome.reason, provider);
  }
}

/**
 * THE N3 ANSWER (plan 3c interface D13). Both reasons get a screen that names
 * the situation in plain German and links the provider's own revocation page,
 * because the operator looking at it is the only person who can act:
 *
 *  - `foreignFlow`: the connection attempt does not belong to this account.
 *    Either the server refused to consume the state (`400 invalid_state`, the
 *    session that started the flow was not this one) or the URL carried a
 *    state our callback never minted. A generic "Verbindung fehlgeschlagen"
 *    here would be the wrong answer to the one error whose most likely cause
 *    is an attack.
 *  - `browserMismatch`: A1's callback refused to park the code because this
 *    browser presented no binding cookie. Two honest readings, and the copy
 *    carries both: the flow was started somewhere else (another browser,
 *    blocked cookies) -- or somebody sent this link and the consent just given
 *    was meant to land in their account.
 *
 * NEITHER offers a retry button. "Erneut versuchen" on a screen that says a
 * grant may have gone to a stranger is the wrong next action; revoking is.
 */
function securityScreen(reason: SecurityReason, provider: Provider): ConnectScreen {
  const name = providerName(provider);
  const base = {
    tone: 'security' as const,
    role: 'alert' as const,
    revocation: revocationLink(provider),
    supportNote: t('connect.security.support', { mail: SUPPORT_MAIL }),
    retry: null,
  };
  switch (reason) {
    case 'foreignFlow':
      return { ...base, title: t('connect.security.foreign.title'), body: t('connect.security.foreign.body', { provider: name }) };
    case 'browserMismatch':
      return { ...base, title: t('connect.security.mismatch.title'), body: t('connect.security.mismatch.body', { provider: name }) };
  }
}
